import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import MarketingShell from "@/components/marketing/MarketingShell";
import { useProductName } from "@/context/BrandContext";
import {
  ArrowRight, GitBranch, Bot, Mic2, BookOpen, PhoneForwarded, Voicemail,
  Radio, BarChart3, Gauge, Users, Server, Wallet, Shield, Sparkles,
  Headphones, Languages, PauseCircle, FileText, PhoneCall, Play, Pause,
} from "lucide-react";

/* ---------- Feature visuals (product-style mocks) ---------- */

function IlluFlow({ className = "" }) {
  return (
    <div
      className={`relative overflow-hidden rounded-3xl border border-border bg-card shadow-lg ${className}`}
      aria-hidden
      data-testid="illu-flow"
    >
      {/* Window chrome */}
      <div className="flex h-11 items-center gap-2 border-b border-border bg-muted/70 px-4">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 font-mono text-[11px] text-muted-foreground">campaign · outbound-qualify</span>
        <span className="ml-auto rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-semibold text-primary">Draft</span>
      </div>

      <div className="relative grid min-h-[280px] grid-cols-[72px_1fr] sm:min-h-[320px]">
        {/* Node palette */}
        <div className="flex flex-col items-center gap-3 border-r border-border bg-muted/40 py-4">
          {[
            { Icon: GitBranch, tip: "Branch" },
            { Icon: Bot, tip: "AI" },
            { Icon: Mic2, tip: "Audio" },
            { Icon: PhoneForwarded, tip: "Transfer" },
          ].map(({ Icon, tip }) => (
            <div
              key={tip}
              title={tip}
              className="grid h-10 w-10 place-items-center rounded-xl border border-border bg-background text-primary shadow-sm transition-transform hover:-translate-y-0.5"
            >
              <Icon className="h-4 w-4" />
            </div>
          ))}
          <div className="mt-auto grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md m-float">
            <span className="text-lg font-bold leading-none">+</span>
          </div>
        </div>

        {/* Canvas */}
        <div
          className="relative p-5 sm:p-6"
          style={{
            backgroundImage:
              "radial-gradient(hsl(var(--border)) 1px, transparent 1px)",
            backgroundSize: "18px 18px",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.06] via-transparent to-emerald-500/[0.05]" />

          <div className="relative mx-auto flex max-w-sm flex-col items-center gap-0">
            {/* Start */}
            <div className="rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-md shadow-primary/25">
              Start
            </div>
            <div className="h-6 w-px bg-primary/50" />
            <div className="h-2 w-2 rounded-full bg-primary" />
            <div className="h-4 w-px bg-primary/40" />

            {/* AI Agent */}
            <div className="w-full rounded-2xl border-2 border-primary/70 bg-background p-4 shadow-md">
              <div className="flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary">
                  <Bot className="h-5 w-5" />
                </div>
                <div>
                  <p className="disp text-sm font-semibold">AI Agent</p>
                  <p className="text-[11px] text-muted-foreground">Live · Call script · Pause on talk</p>
                </div>
              </div>
            </div>

            {/* Branch lines */}
            <svg className="h-10 w-full text-primary/50" viewBox="0 0 280 40" fill="none" aria-hidden>
              <path d="M140 0v12M140 12H70M140 12h70M70 12v28M210 12v28" stroke="currentColor" strokeWidth="2" />
              <circle cx="70" cy="36" r="3" fill="currentColor" />
              <circle cx="210" cy="36" r="3" fill="currentColor" />
            </svg>

            {/* Outcomes */}
            <div className="grid w-full grid-cols-2 gap-3">
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-3 text-center">
                <PhoneForwarded className="mx-auto mb-1 h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">Transfer</p>
                <p className="text-[10px] text-muted-foreground">to human SIP</p>
              </div>
              <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-3 text-center">
                <PhoneCall className="mx-auto mb-1 h-4 w-4 text-rose-600 dark:text-rose-400" />
                <p className="text-xs font-semibold text-rose-700 dark:text-rose-300">Hangup</p>
                <p className="text-[10px] text-muted-foreground">end call</p>
              </div>
            </div>
          </div>

          <p className="relative mt-5 text-center text-[11px] font-medium tracking-wide text-muted-foreground">
            Drag · Connect · Launch
          </p>
        </div>
      </div>
    </div>
  );
}

function IlluAgent({ className = "" }) {
  return (
    <div className={`relative overflow-hidden rounded-3xl border border-border bg-card shadow-lg ${className}`} aria-hidden>
      <div className="flex h-11 items-center gap-2 border-b border-border bg-muted/70 px-4">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 font-mono text-[11px] text-muted-foreground">ai-agent · live duplex</span>
        <span className="ml-auto flex items-center gap-1.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> LIVE
        </span>
      </div>
      <div className="grid gap-4 p-5 sm:grid-cols-2 sm:p-6">
        <div className="rounded-2xl border border-border bg-muted/40 p-5 text-center">
          <div className="mx-auto mb-3 grid h-16 w-16 place-items-center rounded-full bg-primary/15 ring-4 ring-primary/10">
            <Bot className="h-8 w-8 text-primary" />
          </div>
          <p className="disp text-sm font-semibold">AI Agent</p>
          <p className="mt-1 text-[11px] text-muted-foreground">Speaking · en-US · WaveNet</p>
          <div className="mt-4 flex items-end justify-center gap-1 h-10">
            {[8, 18, 12, 28, 16, 24, 10, 22, 14].map((h, i) => (
              <span
                key={i}
                className="w-1.5 rounded-full bg-primary m-float"
                style={{ height: h, animationDelay: `${i * 0.08}s` }}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col justify-center gap-3">
          {[
            { t: "Customer talks", d: "Barge-in pauses AI", on: true },
            { t: "Knowledge answer", d: "Grounded from KB", on: false },
            { t: "Warm transfer", d: "Exit → human SIP", on: false },
          ].map((row) => (
            <div key={row.t} className={`rounded-xl border px-3 py-2.5 ${row.on ? "border-primary/40 bg-primary/10" : "border-border bg-background"}`}>
              <p className="text-xs font-semibold">{row.t}</p>
              <p className="text-[10px] text-muted-foreground">{row.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function IlluCapacityChart({ className = "" }) {
  const bars = [
    { d: "Mon", v: 42 }, { d: "Tue", v: 68 }, { d: "Wed", v: 55 },
    { d: "Thu", v: 82 }, { d: "Fri", v: 74 }, { d: "Sat", v: 91 }, { d: "Sun", v: 63 },
  ];
  return (
    <div className={`rounded-3xl border border-border bg-card p-6 shadow-lg ${className}`} aria-hidden>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="disp text-base font-bold">Concurrent channels</p>
          <p className="text-xs text-muted-foreground">Live usage vs account limit</p>
        </div>
        <span className="rounded-full bg-rose-500/10 px-2.5 py-1 text-[10px] font-semibold text-rose-600 dark:text-rose-400">
          Limit 70
        </span>
      </div>
      <div className="relative mt-8 flex h-40 items-end gap-2 sm:gap-3">
        <div className="pointer-events-none absolute inset-x-0 top-[28%] border-t border-dashed border-rose-400/70" />
        {bars.map((b) => (
          <div key={b.d} className="flex flex-1 flex-col items-center gap-2">
            <div className="relative w-full max-w-[36px]">
              <div
                className="w-full rounded-t-lg bg-primary/20"
                style={{ height: `${b.v * 1.4}px` }}
              />
              <div
                className="absolute bottom-0 w-full rounded-t-lg bg-primary"
                style={{ height: `${Math.min(b.v, 70) * 1.4}px` }}
              />
            </div>
            <span className="text-[10px] text-muted-foreground">{b.d}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IlluTenancy({ className = "" }) {
  return (
    <div className={`rounded-3xl border border-border bg-card p-6 shadow-lg ${className}`} aria-hidden>
      <div className="mx-auto max-w-xs rounded-2xl bg-primary px-4 py-3 text-center text-sm font-bold text-primary-foreground shadow-md shadow-primary/20">
        Platform (Admin)
      </div>
      <div className="mx-auto my-2 h-6 w-px bg-border" />
      <div className="grid grid-cols-2 gap-3">
        {["Reseller A", "Reseller B"].map((r) => (
          <div key={r} className="rounded-2xl border border-border bg-muted/50 p-3">
            <p className="text-center text-xs font-semibold">{r}</p>
            <div className="mx-auto my-2 h-4 w-px bg-border" />
            <div className="grid gap-1.5">
              {["Customer", "Customer"].map((c, i) => (
                <div key={`${r}-${i}`} className="rounded-lg border border-primary/25 bg-primary/10 px-2 py-1.5 text-center text-[10px] font-medium text-primary">
                  {c}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-center text-[11px] text-muted-foreground">Isolated wallets · roles · impersonation</p>
    </div>
  );
}

function IlluLive({ className = "" }) {
  const rows = [
    { n: "+1 (555) 012-3300", s: "qualifying" },
    { n: "+1 (555) 012-3301", s: "transferring" },
    { n: "+1 (555) 012-3302", s: "listening" },
  ];
  return (
    <div className={`rounded-3xl border border-border bg-card shadow-lg ${className}`} aria-hidden>
      <div className="flex h-11 items-center justify-between border-b border-border bg-muted/70 px-4">
        <span className="disp text-sm font-semibold">Live Calls</span>
        <span className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> 3 active
        </span>
      </div>
      <div className="space-y-2.5 p-4">
        {rows.map((r, i) => (
          <div key={r.n} className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-3">
            <span className="relative flex h-3 w-3">
              <span
                className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60"
                style={{ animation: "m-pulse-ring 1.8s ease-out infinite", animationDelay: `${i * 0.25}s` }}
              />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{r.n}</p>
              <p className="text-[11px] text-muted-foreground">AI Agent · {r.s}</p>
            </div>
            <span className="font-mono text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">LIVE</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DonutBilling({ className = "" }) {
  return (
    <div className={`rounded-3xl border border-border bg-card p-6 shadow-lg ${className}`} aria-hidden>
      <p className="disp text-base font-bold">Wallet health</p>
      <p className="text-xs text-muted-foreground">Cash balance vs channel spend</p>
      <div className="mt-6 flex flex-col items-center gap-6 sm:flex-row sm:justify-center">
        <div className="relative h-36 w-36">
          <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
            <circle cx="60" cy="60" r="46" fill="none" stroke="hsl(var(--border))" strokeWidth="14" />
            <circle cx="60" cy="60" r="46" fill="none" stroke="hsl(var(--primary))" strokeWidth="14"
              strokeDasharray="220 289" strokeLinecap="round" />
            <circle cx="60" cy="60" r="46" fill="none" stroke="hsl(152 55% 42%)" strokeWidth="14"
              strokeDasharray="55 289" strokeDashoffset="-220" strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 grid place-items-center text-center">
            <div>
              <p className="disp text-xl font-bold">$5k</p>
              <p className="text-[10px] text-muted-foreground">cash wallet</p>
            </div>
          </div>
        </div>
        <ul className="space-y-2.5 text-sm">
          {[
            { c: "bg-primary", t: "Channels used" },
            { c: "bg-emerald-500", t: "Available" },
            { c: "bg-border", t: "Reserved" },
          ].map((l) => (
            <li key={l.t} className="flex items-center gap-2">
              <span className={`h-3 w-3 rounded ${l.c}`} /> {l.t}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const FEATURE_GROUPS = [
  {
    id: "design",
    eyebrow: "Campaign studio",
    title: "Design every conversation visually",
    blurb: "Build outbound and inbound journeys on an infinite canvas — no code, no guesswork.",
    Illu: IlluFlow,
    items: [
      { icon: GitBranch, title: "Visual Flow Designer",
        body: "Drag nodes for Start, Play Audio, Wait, Silence, Intent, DTMF, AI Agent, Collect Info, Transfer, Hangup, and End. Connect branches and save the campaign flow." },
      { icon: FileText, title: "Call Script on AI Agent",
        body: "Write IF-style scripts (reception vs owner) so the agent opens and manages the call from your playbook." },
      { icon: Sparkles, title: "Global Campaign Templates",
        body: "Admins publish reusable campaign templates customers can clone into their own accounts." },
      { icon: Headphones, title: "Test Call",
        body: "Preview a flow in the browser with live mic audio before you put real traffic on the campaign." },
    ],
  },
  {
    id: "ai",
    eyebrow: "Conversational AI",
    title: "AI Agents that talk like your team",
    blurb: "Full-duplex Live voice, knowledge answers, and human-like turn taking.",
    Illu: IlluAgent,
    items: [
      { icon: Bot, title: "AI Agent (Live duplex)",
        body: "Realtime Gemini Live audio on the phone — greet, qualify, answer, and exit via flow actions (continue, collect, transfer, hangup)." },
      { icon: PauseCircle, title: "Pause when customer talks",
        body: "Per-node barge-in: if the customer speaks while the AI is talking, the agent stops and listens for true two-way conversation." },
      { icon: Languages, title: "Languages & voices",
        body: "Pick the speaking language and TTS / Live voice for each AI Agent node so campaigns match your market." },
      { icon: BookOpen, title: "Knowledge Bases",
        body: "Attach product/FAQ knowledge so the agent answers from your content instead of inventing facts." },
      { icon: Mic2, title: "Text to Speech & STT",
        body: "Customer TTS voices for prompts, paid / Piper catalogs, and speech recognition for collect-info and turn-based paths." },
    ],
  },
  {
    id: "telephony",
    eyebrow: "Telephony & routing",
    title: "Carrier-grade call control",
    blurb: "AMD, warm transfer, VICIdial, and engine routing on your own SIP stack.",
    Illu: IlluLive,
    items: [
      { icon: Voicemail, title: "Answering Machine Detection",
        body: "Classify human vs voicemail early, play a drop message when needed, and keep agents off machine traffic." },
      { icon: PhoneForwarded, title: "Warm human transfer",
        body: "Bridge the live leg to SIP or DID destinations through OpenSIPS — context preserved, audio continuous." },
      { icon: Server, title: "VICIdial · OpenSIPS · Media · AI Engines",
        body: "Register dialers, proxies, media hosts, and AI engine servers. Capacity-aware /route picks a healthy engine per call." },
      { icon: Radio, title: "Live Calls board",
        body: "Watch active sessions, phone numbers, AI state, and transfers update in real time across your fleet." },
    ],
  },
  {
    id: "ops",
    eyebrow: "Ops & billing",
    title: "Multi-tenant control plane",
    blurb: "Roles, wallets, concurrent limits, CDRs, and reports for the whole channel.",
    Illu: IlluTenancy,
    items: [
      { icon: Users, title: "Admin · Reseller · Customer",
        body: "Strict tenant isolation with impersonation for support, reseller sub-accounts, and customer self-serve." },
      { icon: Gauge, title: "Concurrent call limits",
        body: "Buy and renew channel capacity from cash balance. Race-safe admission rejects over-limit calls with clear CDRs." },
      { icon: Wallet, title: "Billing, payments & pricing",
        body: "Cash wallets, payment approval inbox, standard/custom channel pricing, and reseller rate cards." },
      { icon: BarChart3, title: "CDR, Reports & Stats",
        body: "Full call detail records including AI Agent duration, ledger/reservation reports, and admin stats." },
      { icon: Shield, title: "System settings & logs",
        body: "Platform defaults, chat widget, intro videos, routing secrets in Mongo, and searchable system/telephony logs." },
    ],
  },
];

const QUICK = [
  { t: "Flow designer", d: "Visual call graphs" },
  { t: "AI Live agent", d: "Realtime voice" },
  { t: "Knowledge", d: "Grounded answers" },
  { t: "AMD", d: "Human vs machine" },
  { t: "Warm transfer", d: "SIP / DID handoff" },
  { t: "Live board", d: "Active calls" },
  { t: "Call limits", d: "Concurrency caps" },
  { t: "Multi-tenant", d: "Admin → Customer" },
];

/** Animated AI bot for the Features hero right column. */
function FeaturesHeroBot({ homeDemo, playing, onTogglePlay }) {
  return (
    <div className="relative mx-auto w-full max-w-md lg:max-w-none" data-testid="features-hero-bot">
      <style>{`
        @keyframes feat-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes feat-blink { 0%,42%,48%,100%{transform:scaleY(1)} 45%{transform:scaleY(.12)} }
        @keyframes feat-wave { 0%{transform:scaleY(.3)} 50%{transform:scaleY(1)} 100%{transform:scaleY(.35)} }
        @keyframes feat-orbit { to { transform: rotate(360deg); } }
        @keyframes feat-pulse { 0%{transform:scale(.9);opacity:.45} 70%{transform:scale(1.2);opacity:0} 100%{opacity:0} }
        @keyframes feat-glow { 0%,100%{opacity:.55} 50%{opacity:1} }
        @keyframes feat-play-ripple {
          0% { transform: scale(1); opacity: 0.55; }
          70% { transform: scale(1.9); opacity: 0; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        .feat-float{animation:feat-float 4.8s ease-in-out infinite}
        .feat-blink{transform-box:fill-box;transform-origin:center;animation:feat-blink 4.2s ease-in-out infinite}
        .feat-wave{transform-box:fill-box;transform-origin:center bottom;animation:feat-wave 1s ease-in-out infinite}
        .feat-orbit{transform-box:fill-box;transform-origin:240px 210px;animation:feat-orbit 18s linear infinite}
        .feat-pulse{transform-box:fill-box;transform-origin:center;animation:feat-pulse 2.6s ease-out infinite}
        .feat-glow{animation:feat-glow 2.2s ease-in-out infinite}
        .feat-play-ripple{
          position:absolute; inset:0; border-radius:9999px;
          border:2px solid hsl(var(--primary) / 0.55);
          animation:feat-play-ripple 2s ease-out infinite;
          pointer-events:none;
        }
        .feat-play-ripple-cyan{
          border-color: rgb(34 211 238 / 0.55);
        }
      `}</style>
      <div className="feat-float relative">
        <svg viewBox="0 0 480 420" className="block h-auto w-full" role="img" aria-label="AI voice bot animation">
          <defs>
            <linearGradient id="feat-halo" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.2" />
              <stop offset="100%" stopColor="hsl(190 70% 40%)" stopOpacity="0.06" />
            </linearGradient>
            <linearGradient id="feat-body" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#eef2ff" />
            </linearGradient>
            <linearGradient id="feat-face" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#0f172a" />
              <stop offset="100%" stopColor="#1e293b" />
            </linearGradient>
            <linearGradient id="feat-accent" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" />
              <stop offset="100%" stopColor="#22d3ee" />
            </linearGradient>
            <filter id="feat-soft" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="6" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {/* Soft stage */}
          <circle cx="240" cy="210" r="168" fill="url(#feat-halo)" />
          <circle cx="240" cy="210" r="132" className="feat-pulse" fill="none" stroke="hsl(var(--primary))" strokeOpacity="0.28" strokeWidth="2" />
          <circle cx="240" cy="210" r="132" className="feat-pulse" style={{ animationDelay: "0.9s" }} fill="none" stroke="hsl(190 70% 40%)" strokeOpacity="0.2" strokeWidth="1.5" />

          {/* Orbiting dots */}
          <g className="feat-orbit">
            <circle cx="240" cy="58" r="5" fill="hsl(var(--primary))" className="feat-glow" />
            <circle cx="390" cy="250" r="4" fill="#22d3ee" opacity="0.85" />
            <circle cx="100" cy="280" r="3.5" fill="hsl(var(--primary))" opacity="0.7" />
          </g>

          {/* Bot body */}
          <g transform="translate(140 70)">
            {/* Antenna */}
            <line x1="100" y1="28" x2="100" y2="8" stroke="hsl(var(--primary))" strokeWidth="3" strokeLinecap="round" />
            <circle cx="100" cy="4" r="7" fill="url(#feat-accent)" className="feat-glow" filter="url(#feat-soft)" />

            {/* Head */}
            <rect x="28" y="28" width="144" height="128" rx="36" fill="url(#feat-body)" stroke="hsl(var(--border))" strokeWidth="2" />
            <rect x="42" y="46" width="116" height="78" rx="22" fill="url(#feat-face)" />

            {/* Eyes */}
            <g className="feat-blink">
              <ellipse cx="78" cy="82" rx="12" ry="14" fill="url(#feat-accent)" className="feat-glow" />
              <ellipse cx="122" cy="82" rx="12" ry="14" fill="url(#feat-accent)" className="feat-glow" />
              <circle cx="82" cy="78" r="3.5" fill="#fff" opacity="0.9" />
              <circle cx="126" cy="78" r="3.5" fill="#fff" opacity="0.9" />
            </g>

            {/* Waveform mouth */}
            <g transform="translate(62 112)" fill="hsl(var(--primary))">
              {[8, 14, 20, 12, 18, 10, 16, 9].map((h, i) => (
                <rect
                  key={i}
                  className={`feat-wave${playing ? "" : ""}`}
                  x={i * 10}
                  y={20 - h}
                  width="5"
                  height={h}
                  rx="2.5"
                  style={{
                    animationDelay: `${i * 0.07}s`,
                    animationPlayState: playing ? "running" : "paused",
                  }}
                />
              ))}
            </g>

            {/* Neck */}
            <rect x="88" y="156" width="24" height="18" rx="6" fill="#c7d2fe" />

            {/* Torso — hide chest orb when play control overlays it */}
            <rect x="48" y="172" width="104" height="88" rx="28" fill="url(#feat-body)" stroke="hsl(var(--border))" strokeWidth="2" />
            {!homeDemo && (
              <>
                <circle cx="100" cy="210" r="18" fill="url(#feat-accent)" opacity="0.9" className="feat-glow" />
                <circle cx="100" cy="210" r="8" fill="#fff" opacity="0.85" />
              </>
            )}

            {/* Arms */}
            <rect x="14" y="184" width="28" height="52" rx="14" fill="url(#feat-body)" stroke="hsl(var(--border))" strokeWidth="1.5" />
            <rect x="158" y="184" width="28" height="52" rx="14" fill="url(#feat-body)" stroke="hsl(var(--border))" strokeWidth="1.5" />
          </g>

          {/* Floating status chips */}
          <g transform="translate(18 120)">
            <rect width="118" height="44" rx="12" fill="#fff" stroke="hsl(var(--border))" strokeWidth="1.5" />
            <circle cx="22" cy="22" r="8" fill="#10b981" opacity="0.2" />
            <circle cx="22" cy="22" r="4" fill="#10b981" className="feat-glow" />
            <text x="38" y="19" fill="#0f172a" fontSize="11" fontWeight="600" fontFamily="ui-sans-serif, system-ui, sans-serif">Listening</text>
            <text x="38" y="34" fill="#64748b" fontSize="10" fontFamily="ui-sans-serif, system-ui, sans-serif">Realtime STT</text>
          </g>
          <g transform="translate(330 250)">
            <rect width="128" height="44" rx="12" fill="#fff" stroke="hsl(var(--border))" strokeWidth="1.5" />
            <circle cx="22" cy="22" r="8" fill="hsl(var(--primary))" opacity="0.18" />
            <path d="M16 22c2-6 6-9 10-9s8 3 10 9" fill="none" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" />
            <text x="40" y="19" fill="#0f172a" fontSize="11" fontWeight="600" fontFamily="ui-sans-serif, system-ui, sans-serif">Speaking</text>
            <text x="40" y="34" fill="#64748b" fontSize="10" fontFamily="ui-sans-serif, system-ui, sans-serif">Neural TTS</text>
          </g>
        </svg>

        {/* Circular play on bot chest — ripples when idle (distinct from home glass pill) */}
        {homeDemo && (
          <div className="pointer-events-none absolute left-1/2 top-[66.5%] z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center">
            <button
              type="button"
              data-testid="features-demo-play"
              onClick={onTogglePlay}
              aria-label={playing ? `Pause ${homeDemo.title}` : `Play ${homeDemo.title}`}
              className="pointer-events-auto relative grid h-14 w-14 place-items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 sm:h-16 sm:w-16"
            >
              {!playing && (
                <>
                  <span className="feat-play-ripple" />
                  <span className="feat-play-ripple feat-play-ripple-cyan" style={{ animationDelay: "0.65s" }} />
                  <span className="feat-play-ripple" style={{ animationDelay: "1.3s" }} />
                </>
              )}
              <span className="relative z-[1] grid h-14 w-14 place-items-center rounded-full border-2 border-white bg-primary text-primary-foreground shadow-[0_8px_24px_-6px_hsl(var(--primary)/0.55)] transition-transform hover:scale-110 sm:h-16 sm:w-16">
                {playing
                  ? <Pause className="h-6 w-6 sm:h-7 sm:w-7" fill="currentColor" />
                  : <Play className="h-6 w-6 translate-x-0.5 sm:h-7 sm:w-7" fill="currentColor" />}
              </span>
            </button>
            <p
              className="mt-2.5 max-w-[9rem] truncate rounded-full border border-border bg-background/90 px-2.5 py-0.5 text-center text-[10px] font-semibold text-foreground shadow-sm backdrop-blur-sm"
              data-testid="features-demo-title"
            >
              {playing ? "Playing" : "Demo"} · {homeDemo.title}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Features() {
  const productName = useProductName();
  const [homeDemo, setHomeDemo] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    api.get("/settings/public")
      .then(({ data }) => setHomeDemo(data?.home_demo_call || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const el = document.getElementById("features-demo-audio");
    if (!el) return;
    if (playing) {
      el.play().catch(() => setPlaying(false));
    } else {
      el.pause();
    }
  }, [playing, homeDemo?.id]);

  const togglePlay = () => {
    if (!homeDemo) return;
    setPlaying((p) => !p);
  };

  return (
    <MarketingShell active="features">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div
          className="pointer-events-none absolute inset-0 opacity-80"
          style={{
            background:
              "radial-gradient(900px 420px at 15% -10%, hsl(var(--primary) / 0.18), transparent 60%), radial-gradient(700px 380px at 90% 10%, hsl(190 70% 40% / 0.12), transparent 55%)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-6 pb-16 pt-16 sm:pt-20">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
            <div className="m-rise max-w-3xl">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Features</p>
              <h1 className="disp mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Everything in the {productName || "voice"} control plane
              </h1>
              <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
                From visual campaign design and Live AI agents to AMD, warm transfer, concurrent billing,
                and multi-tenant ops — one platform for voice at scale.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Get started <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="#design"
                  className="inline-flex items-center gap-2 rounded-xl border border-border px-6 py-3.5 text-sm font-medium text-foreground hover:bg-muted"
                >
                  Browse by area
                </a>
                {homeDemo && (
                  <button
                    type="button"
                    onClick={togglePlay}
                    data-testid="features-demo-play-mobile"
                    className="inline-flex items-center gap-2 rounded-xl border border-border px-6 py-3.5 text-sm font-medium text-foreground hover:bg-muted md:hidden"
                  >
                    {playing
                      ? <><Pause className="h-4 w-4" /> Pause demo</>
                      : <><Play className="h-4 w-4" /> Play demo</>}
                  </button>
                )}
              </div>
            </div>
            <div className="m-rise hidden md:block" style={{ animationDelay: "0.12s" }}>
              <FeaturesHeroBot
                homeDemo={homeDemo}
                playing={playing}
                onTogglePlay={togglePlay}
              />
            </div>
          </div>
          {homeDemo && (
            <audio
              id="features-demo-audio"
              key={homeDemo.id}
              src={`${API}/settings/demo-calls/${homeDemo.id}/audio`}
              preload="none"
              onEnded={() => setPlaying(false)}
              data-testid="features-demo-audio"
            />
          )}

          {/* Quick chips */}
          <div className="m-rise mt-12 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8" style={{ animationDelay: "0.1s" }}>
            {QUICK.map((q) => (
              <div
                key={q.t}
                className="rounded-2xl border border-border bg-card/70 px-3 py-3 text-center backdrop-blur-sm"
              >
                <p className="disp text-sm font-semibold">{q.t}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{q.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capacity chart band */}
      <section className="border-b border-border bg-muted/30">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 py-16 lg:grid-cols-2">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Scale with confidence</p>
            <h2 className="disp mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              Concurrent channels you can meter
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Buy channel capacity from cash balance, renew monthly, and let race-safe admission protect
              every account. Over-limit calls are rejected cleanly and logged on the CDR.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-foreground">
              {["Per-customer and reseller limits", "Wallet reservations & approvals", "AI Agent talk-time on CDRs"].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <IlluCapacityChart className="w-full drop-shadow-sm" />
        </div>
      </section>

      {/* Feature groups */}
      {FEATURE_GROUPS.map((g, gi) => (
        <section
          key={g.id}
          id={g.id}
          className={`border-b border-border ${gi % 2 === 1 ? "bg-muted/30" : ""}`}
        >
          <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
            <div className="mx-auto max-w-2xl text-center lg:mx-0 lg:max-w-xl lg:text-left">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">{g.eyebrow}</p>
              <h2 className="disp mt-3 text-3xl font-bold tracking-tight sm:text-4xl">{g.title}</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">{g.blurb}</p>
            </div>

            <div className={`mt-10 grid items-stretch gap-8 lg:grid-cols-12 ${gi % 2 === 1 ? "" : ""}`}>
              <div className={`lg:col-span-7 ${gi % 2 === 1 ? "lg:order-2" : ""}`}>
                <g.Illu className="h-full w-full" />
              </div>
              <div className={`flex flex-col gap-3 lg:col-span-5 ${gi % 2 === 1 ? "lg:order-1" : ""}`}>
                {g.items.map((item) => (
                  <article
                    key={item.title}
                    data-testid={`feature-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex gap-3 rounded-2xl border border-border bg-card/80 p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/35 hover:shadow-md"
                  >
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="disp text-base font-semibold">{item.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Billing visual */}
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 py-16 lg:grid-cols-2">
          <DonutBilling className="w-full" />
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Money & metering</p>
            <h2 className="disp mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Wallets that match how you sell voice</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Cash payments feed the wallet. Channel purchases and renewals draw from it. Admins and
              resellers set pricing; customers see clear balances and transaction history.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div
          className="relative overflow-hidden rounded-3xl border border-border px-8 py-14 text-center sm:px-16"
          style={{
            background:
              "linear-gradient(135deg, hsl(var(--primary) / 0.14), hsl(var(--muted) / 0.5))",
          }}
        >
          <PhoneCall className="mx-auto mb-4 h-8 w-8 text-primary" />
          <h2 className="disp text-3xl font-bold tracking-tight sm:text-4xl">
            Put these features on your numbers
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Create an account, design a flow, and launch AI voice campaigns on your telephony stack.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground"
            >
              Create account <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-background/60 px-7 py-3.5 text-sm font-medium"
            >
              Sign in
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
